import {
  Stack,
  Typography,
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from '@mui/material';
import { useEffect, useState } from 'react';

import { extractErrorMessage } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/errorHandling';
import {
  EncryptionComponent,
  getEncryptionKey,
  type Encryption,
} from '@/components/encryption/encryptionComponent';
import { AvailabilityZoneSelect } from '@/components/selectors/availabilityZoneSelect';
import { SnapshotToolbar } from '@/components/snapshots/snapshotToolbar';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';
import { PreferencesKey } from '@/data/dal/preferencesKey';

import { StepContainer } from '../../wizard/StepContainer';
import type { RestoreEKSNamespaceState } from '../restoreEksNamespaceWizard';

export const EksVolumesConfiguration = (
  props: StepProps<RestoreEKSNamespaceState>
) => {
  const { stepperLabels, back, finish, wizardState, setWizardState } = props;
  const dal = useDAL();
  const [isRestoring, setIsRestoring] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [encryptions, setEncryptions] = useState<Encryption[]>([]);

  const { data: snapshotsBody, isLoading: isLoadingSnapshot } =
    dal.inventory.snapshots.useSnapshot(wizardState.snapshot.id);

  const { data: originAZsBody, isLoading: isLoadingSourceAZs } =
    dal.restore.availabilityZones.useList(
      wizardState.restoreAccount!.id,
      wizardState.resource.region
    );

  const { data: targetAZsBody, isLoading: isLoadingTargetAZs } =
    dal.restore.availabilityZones.useList(
      wizardState.restoreAccount!.id,
      wizardState.region
    );

  const volumeLabels = Object.fromEntries(
    (
      snapshotsBody?.snapshot?.resource?.properties?.awsEksNamespace?.volumes ??
      []
    ).map(({ providerVolumeId, volumeLabel }) => [
      providerVolumeId,
      volumeLabel ?? providerVolumeId,
    ])
  ) as Record<string, string>;

  useEffect(() => {
    if (
      isLoadingSnapshot ||
      isLoadingSourceAZs ||
      isLoadingTargetAZs ||
      !snapshotsBody ||
      !originAZsBody ||
      !targetAZsBody
    ) {
      return;
    }

    const volumes =
      snapshotsBody.snapshot.resource?.properties?.awsEksNamespace?.volumes ||
      [];

    setEncryptions(
      volumes.map(() => ({
        isEncrypted: false,
        mode: 'select',
        arn: undefined,
        encryptionKeyId: undefined,
      }))
    );

    if (originAZsBody.region === targetAZsBody.region) {
      return setWizardState((state) => ({
        ...state,
        volumes,
      }));
    }

    const originAZs = originAZsBody.availabilityZones.sort();
    const targetAZs = targetAZsBody.availabilityZones.sort();

    setWizardState((state) => ({
      ...state,
      volumes: volumes.map((x) => {
        const sourceAzIndex = Math.max(
          originAZs.indexOf(x.availabilityZone),
          0
        );

        const targetAz = targetAZs[sourceAzIndex % targetAZs.length];
        return { ...x, availabilityZone: targetAz };
      }),
    }));
  }, [
    snapshotsBody,
    setWizardState,
    originAZsBody,
    targetAZsBody,
    isLoadingSnapshot,
    isLoadingSourceAZs,
    isLoadingTargetAZs,
  ]);

  return (
    <StepContainer
      sx={{ padding: '0' }}
      stepperLabels={stepperLabels}
      stepperIndex={stepperLabels.length - 1}
      nextButtonText='Restore'
      nextButtonLoadingText='Restoring...'
      onBackClick={back}
      canGoNext={() => {
        if (
          isRestoring ||
          isLoadingSnapshot ||
          isLoadingSourceAZs ||
          isLoadingTargetAZs ||
          !wizardState.volumes.every((vol) => vol.availabilityZone)
        ) {
          return false;
        }

        if (props.wizardState.sharedEncryption?.isEncrypted) {
          return !!getEncryptionKey(props.wizardState.sharedEncryption);
        }

        return encryptions.every((encryption) => {
          if (!encryption.isEncrypted) {
            return true;
          }

          return !!getEncryptionKey(encryption);
        });
      }}
      onNextClick={async () => {
        setIsRestoring(true);
        setError(undefined);
        const bucketName = wizardState.S3.Bucket ?? '';

        void dal.preferences.updateUserPref(PreferencesKey.RestoreBucket, {
          bucketName,
        });

        const tags = wizardState.resource.tags || {};

        return dal.restore.eks
          .restoreNamespace(
            wizardState.snapshot.resourceId,
            wizardState.snapshot.id,
            {
              tags: { ...tags, 'eon:restore': 'true' },
              region: wizardState.region!,
              restoreAccountId: wizardState.restoreAccount!.id,
              filesTarget: {
                bucketName,
                prefix: wizardState.prefix,
              },
              volumeSettings: wizardState.volumes.map((vol, index) => {
                const tags = vol.tags || {};
                tags['eon:restore'] = 'true';

                const currEncryption = props.wizardState.sharedEncryption
                  ?.isEncrypted
                  ? props.wizardState.sharedEncryption
                  : encryptions[index];

                const encryptionKey = getEncryptionKey(currEncryption);

                return {
                  availabilityZone: vol.availabilityZone,
                  volumeSettings: vol.volumeSettings,
                  providerVolumeId: vol.providerVolumeId,
                  tags,
                  encryptionKeyId: encryptionKey,
                  volumeEncryptionKeyId: encryptionKey,
                };
              }),
            }
          )
          .then(() =>
            finish(
              `Started restore job for ${wizardState.resource.resourceName}`
            )
          )
          .catch(async (e) => {
            const errorMessage = await extractErrorMessage(e);
            setIsRestoring(false);
            setError(`Failed to restore bucket: ${errorMessage}`);
          });
      }}
      navigationComponent={
        error && (
          <Stack alignItems='center' direction='row' sx={{ color: 'red' }}>
            <i className='ri-error-warning-line mr-[8px] text-[18px]' />
            {error}
          </Stack>
        )
      }
      toolbarComponent={
        <SnapshotToolbar
          pointInTime={wizardState.snapshot.pointInTime}
          vaultId={wizardState.snapshot.vaultId}
          accessDeniedItemsCount={wizardState.snapshot.accessDeniedItemsCount}
          supportPiTR={wizardState.snapshot.supportPiTR}
        />
      }
    >
      <Stack>
        <EncryptionComponent
          title='Encrypt all volumes with the same key'
          canBeTurnedOff={true}
          encryption={props.wizardState.sharedEncryption}
          accountId={props.wizardState.restoreAccount!.id}
          regionName={props.wizardState.region}
          onChange={(encryption) =>
            props.setWizardState((state) => ({
              ...state,
              sharedEncryption: encryption,
            }))
          }
        />
        {wizardState.volumes.map((vol, index) => (
          <Accordion
            key={vol.providerVolumeId}
            defaultExpanded={false}
            className='my-[2px]'
          >
            <AccordionSummary>
              <Typography variant='body1'>
                {volumeLabels[vol.providerVolumeId]}
              </Typography>
            </AccordionSummary>
            <AccordionDetails className='p-0'>
              <Stack className='my-[24px] w-full'>
                <Stack className='mx-[40px] w-1/2'>
                  <AvailabilityZoneSelect
                    accountId={wizardState.restoreAccount?.id}
                    regionName={wizardState.region}
                    availabilityZone={vol.availabilityZone}
                    onChange={(availabilityZone) =>
                      props.setWizardState((state) => ({
                        ...state,
                        volumes: state.volumes.map((v) =>
                          v.providerVolumeId === vol.providerVolumeId
                            ? { ...v, availabilityZone }
                            : v
                        ),
                      }))
                    }
                  />
                </Stack>
                {!props.wizardState.sharedEncryption?.isEncrypted && (
                  <EncryptionComponent
                    title='Encrypt this volume'
                    canBeTurnedOff
                    encryption={encryptions[index]}
                    accountId={wizardState.restoreAccount?.id}
                    regionName={wizardState.region}
                    onChange={(encryption) =>
                      setEncryptions((state) =>
                        state.map((e, i) => (i === index ? encryption : e))
                      )
                    }
                  />
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>
        ))}
      </Stack>
    </StepContainer>
  );
};
